import { z } from 'zod';
export const ListSessionsRequestSchema = z.object({
    projectId: z.string().uuid().optional(),
    limit: z.number().int().positive().max(100).optional().default(20),
    offset: z.number().int().nonnegative().optional().default(0),
    sortBy: z.enum(['started_at', 'updated_at']).optional().default('started_at'),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});
export const SessionSummarySchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    projectId: z.string().uuid().nullable(),
    projectName: z.string().nullable(),
    deviceId: z.string().uuid().nullable(),
    deviceName: z.string().nullable(),
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    promptCount: z.number().int().nonnegative(),
    executionCount: z.number().int().nonnegative(),
});
export const ListSessionsResponseSchema = z.object({
    sessions: z.array(SessionSummarySchema),
    pagination: z.object({
        total: z.number().int().nonnegative(),
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    }),
});
export const GetSessionTimelineRequestSchema = z.object({
    sessionId: z.string().uuid(),
    limit: z.number().int().positive().max(500).optional().default(100),
    offset: z.number().int().nonnegative().optional().default(0),
    types: z.array(z.enum(['prompt', 'execution'])).optional(),
});
export const TimelineItemSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('prompt'),
        id: z.string().uuid(),
        model: z.string().nullable(),
        promptText: z.string().nullable(),
        promptHash: z.string().nullable(),
        responseText: z.string().nullable(),
        inputTokens: z.number().int().nullable(),
        outputTokens: z.number().int().nullable(),
        latencyMs: z.number().int().nullable(),
        createdAt: z.string().datetime(),
    }),
    z.object({
        type: z.literal('execution'),
        id: z.string().uuid(),
        promptId: z.string().uuid().nullable(),
        agent: z.string().nullable(),
        executionType: z.string().nullable(),
        status: z.string(),
        durationMs: z.number().int().nullable(),
        success: z.boolean().nullable(),
        summary: z.string().nullable(),
        errorMessage: z.string().nullable(),
        startedAt: z.string().datetime().nullable(),
        completedAt: z.string().datetime().nullable(),
        createdAt: z.string().datetime(),
    }),
]);
export const GetSessionTimelineResponseSchema = z.object({
    sessionId: z.string().uuid(),
    sessionTitle: z.string(),
    timeline: z.array(TimelineItemSchema),
    pagination: z.object({
        total: z.number().int().nonnegative(),
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    }),
});
export const GetSessionMetricsRequestSchema = z.object({
    sessionId: z.string().uuid(),
});
export const GetSessionMetricsResponseSchema = z.object({
    sessionId: z.string().uuid(),
    metrics: z.object({
        totalPrompts: z.number().int().nonnegative(),
        totalExecutions: z.number().int().nonnegative(),
        totalTokens: z.number().int().nonnegative(),
        totalCost: z.number().int().nonnegative(),
        avgLatency: z.number().nonnegative().nullable(),
        successRate: z.number().min(0).max(1).nullable(),
        duration: z.number().int().nonnegative().nullable(),
    }),
});
export const CreateSessionRequestSchema = z.object({
    title: z.string().min(1).max(200),
    projectId: z.string().uuid().optional(),
    deviceId: z.string().uuid().optional(),
    metadata: z.record(z.any()).optional(),
});
export const CreateSessionResponseSchema = z.object({
    session: z.object({
        id: z.string().uuid(),
        title: z.string(),
        projectId: z.string().uuid().nullable(),
        deviceId: z.string().uuid().nullable(),
        startedAt: z.string().datetime(),
        createdAt: z.string().datetime(),
    }),
});
export const UpdateSessionRequestSchema = z.object({
    sessionId: z.string().uuid(),
    title: z.string().min(1).max(200).optional(),
    endedAt: z.string().datetime().optional(),
    metadata: z.record(z.any()).optional(),
});
export const UpdateSessionResponseSchema = z.object({
    success: z.boolean(),
    session: z.object({
        id: z.string().uuid(),
        title: z.string(),
        updatedAt: z.string().datetime(),
    }),
});
export const CreatePromptRequestSchema = z.object({
    sessionId: z.string().uuid(),
    promptText: z.string().min(1),
    model: z.string().optional(),
    provider: z.string().optional(),
    metadata: z.record(z.any()).optional(),
});
export const CreatePromptResponseSchema = z.object({
    prompt: z.object({
        id: z.string().uuid(),
        sessionId: z.string().uuid(),
        promptText: z.string(),
        model: z.string().nullable(),
        provider: z.string().nullable(),
        createdAt: z.string().datetime(),
    }),
});
export const BatchOperationSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('prompt'),
        sessionId: z.string().uuid(),
        data: z.object({
            id: z.string().uuid(),
            sessionId: z.string().uuid(),
            deviceId: z.string().uuid(),
            agentName: z.string().min(1, 'Agent name is required'),
            promptText: z.string(),
            model: z.string().optional(),
            provider: z.string().optional(),
            metadata: z.record(z.any()).optional(),
            createdAt: z.string().datetime(),
        }),
        timestamp: z.number(),
    }),
    z.object({
        type: z.literal('execution'),
        sessionId: z.string().uuid().optional(),
        executionId: z.string().uuid().optional(),
        data: z.record(z.any()),
        timestamp: z.number(),
    }),
    z.object({
        type: z.literal('tool_usage'),
        sessionId: z.string().uuid().optional(),
        executionId: z.string().uuid().optional(),
        data: z.record(z.any()),
        timestamp: z.number(),
    }),
]);
export const BatchRequestSchema = z.object({
    operations: z.array(BatchOperationSchema).min(1).max(100),
});
export const BatchResponseSchema = z.object({
    processed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    errors: z.array(z.object({
        index: z.number().int().nonnegative(),
        error: z.string(),
    })).optional(),
});
export const SessionErrorResponseSchema = z.object({
    error: z.string(),
    code: z.enum([
        'SESSION_NOT_FOUND',
        'INVALID_SESSION_ID',
        'UNAUTHORIZED_ACCESS',
        'INVALID_PAGINATION',
    ]),
    message: z.string(),
});
//# sourceMappingURL=sessions-contracts.js.map